import { Typography, Box, IconButton, useTheme, AppBar, Container, Button, Menu, MenuItem, useMediaQuery } from "@mui/material";
import { useContext, useState } from "react";
import { ColorModeContext, tokens } from "../../theme";
import MenuIcon from '@mui/icons-material/Menu';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import React from "react";
import logo from './logo.png';
import { Link, useLocation } from "react-router-dom";

const Header = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const colorMode = useContext(ColorModeContext);
    const location = useLocation();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    
    // Mobile menu state
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    
    const handleMenu = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
        window.scrollTo(0, 0);
    };

    const menuItems = [
        { text: 'Home', path: '/' },
        { text: 'Projects', path: '/projects' },
        { text: 'About', path: '/about' },
        { text: 'Contact', path: '/contact' },
    ];

    const colorModeToggle = (
        <IconButton
            onClick={colorMode.toggleColorMode}
            aria-label={theme.palette.mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            sx={{ color: theme.palette.text.primary }}
        >
            {theme.palette.mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
        </IconButton>
    );

    return (
        <AppBar 
            position="sticky" 
            elevation={0}
            sx={{ 
                background: colors.background[200],
                borderBottom: `1px solid ${theme.palette.divider}`,
            }}
        >
            <Container maxWidth="xl">
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    py={1.5}
                    position="relative"
                >
                    {/* Logo */}
                    <Box 
                        component={Link} 
                        to="/"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            textDecoration: 'none',
                            height: '45px',
                        }}
                    >
                        <img
                            src={logo}
                            alt="Logo"
                            style={{
                                height: "100%",
                                width: "auto",
                                objectFit: "contain",
                                maxWidth: "200px",
                            }}
                        />
                    </Box>

                    {/* Desktop Navigation */}
                    {!isMobile && (
                        <Box
                            display="flex"
                            alignItems="center"
                            gap={4}
                        >
                            {menuItems.map((item) => (
                                <Button
                                    key={item.text}
                                    component={Link}
                                    to={item.path}
                                    onClick={() => window.scrollTo(0, 0)}
                                    sx={{
                                        color: location.pathname === item.path 
                                            ? theme.palette.primary.main 
                                            : theme.palette.text.primary,
                                        fontSize: '0.95rem',
                                        fontWeight: 500,
                                        position: 'relative',
                                        '&::after': {
                                            content: '""',
                                            position: 'absolute',
                                            width: location.pathname === item.path ? '100%' : '0%',
                                            height: '2px',
                                            bottom: -4,
                                            left: '50%',
                                            transform: 'translateX(-50%)',
                                            backgroundColor: theme.palette.primary.main,
                                            transition: 'width 0.3s ease-in-out',
                                        },
                                        '&:hover': {
                                            backgroundColor: 'transparent',
                                            color: theme.palette.primary.main,
                                            '&::after': {
                                                width: '100%',
                                            },
                                        },
                                    }}
                                >
                                    {item.text}
                                </Button>
                            ))}
                            {colorModeToggle}
                        </Box>
                    )}

                    {/* Mobile Navigation */}
                    {isMobile && (
                        <>
                            {/* Company Name for Mobile */}
                            <Typography
                                variant="h6"
                                sx={{
                                    color: theme.palette.text.primary,
                                    fontWeight: 700,
                                    fontSize: { xs: '1.15rem', sm: '1.5rem' },
                                    position: 'absolute',
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    letterSpacing: '0.5px',
                                    textTransform: 'uppercase',
                                    fontFamily: 'inherit',
                                    whiteSpace: 'nowrap',
                                    pointerEvents: 'none',
                                    '&::after': {
                                        content: '""',
                                        position: 'absolute',
                                        width: '40%',
                                        height: '2px',
                                        bottom: -4,
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        backgroundColor: theme.palette.primary.main
                                    }
                                }}
                            >
                                AV FACADE
                            </Typography>
                            
                            <Box display="flex" alignItems="center" gap={0.5}>
                                {colorModeToggle}
                                <IconButton
                                    size="large"
                                    edge="end"
                                    onClick={handleMenu}
                                    sx={{
                                        color: theme.palette.text.primary,
                                        padding: 1
                                    }}
                                >
                                    <MenuIcon sx={{ fontSize: 28 }} />
                                </IconButton>
                            </Box>
                            <Menu
                                anchorEl={anchorEl}
                                open={open}
                                onClose={handleClose}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'right',
                                }}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right',
                                }}
                                PaperProps={{
                                    elevation: 3,
                                    sx: {
                                        backgroundColor: colors.background[200],
                                        mt: 1,
                                        minWidth: '200px',
                                        '& .MuiList-root': {
                                            py: 1
                                        }
                                    }
                                }}
                            >
                                {menuItems.map((item) => (
                                    <MenuItem 
                                        key={item.text}
                                        onClick={() => {
                                            setAnchorEl(null);
                                            window.scrollTo(0, 0);
                                        }}
                                        component={Link}
                                        to={item.path}
                                        sx={{
                                            color: location.pathname === item.path 
                                                ? theme.palette.primary.main 
                                                : theme.palette.text.primary,
                                            py: 1.5,
                                            px: 3,
                                            fontSize: '1rem',
                                            fontWeight: location.pathname === item.path ? 600 : 400,
                                            '&:hover': {
                                                color: theme.palette.primary.main,
                                                backgroundColor: colors.background[300],
                                            }
                                        }}
                                    >
                                        <Typography variant="body1">
                                            {item.text}
                                        </Typography>
                                    </MenuItem>
                                ))}
                            </Menu>
                        </>
                    )}
                </Box>
            </Container>
        </AppBar>
    );
};

export default Header;